import React, { useState, useContext } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { useTheme } from '@fluentui/react';
import { Button, makeStyles } from '@fluentui/react-components';
import { EditRegular, CheckmarkCircleRegular } from '@fluentui/react-icons';
import { useQuery } from '@apollo/client';
import Card from '../../common/card/Card';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import SpanWithLabel from '../../common/SpanWithLabel';
import StackColumn from '../../common/StackColumn';
import SafeLink from '../../common/SafeLink';
import {
  bodyContentContainer,
  iconButtonStyles,
  truncatedLinkStyle,
} from '../../../app/common/styles/CommonStyleObjects';
import {
  GET_ENTITY_TERMINATION_COMPLIANCE_PROCESSES,
  GET_ENTITY_TERMINATION_AGM_STATUS_SUMMARY,
  REJECT_ENTITY_TERMINATION_ELIGIBILITY_REVIEW,
  CREATE_ENTITY_TERMINATION_ELIGIBILITY_REVIEW_COMMENT,
  ATTACH_ENTITY_TERMINATION_ELIGIBILITY_REVIEW_DOCUMENT,
  GET_ENTITY_TERMINATION_ELIGIBILITY_REVIEW,
  GET_DORMANCY_ASSESSMENT,
  APPROVE_ENTITY_TERMINATION_ELIGIBILITY_REVIEW,
  GET_COMPANY_LEGAL_STRUCTURE,
} from '../../../utils/statApi/EntityTerminationApi';
import getComplianceSnapshotColumns, { IProcessData } from './ComplianceSnapShotProcesses.config';
import getComplianceSnapshotAGMDetailsColumns, {
  IAGMDetailsData,
} from './ComplianceSnapshotAGMDetails.config';
import ShimmeredDetailsListWrapper from '../../common/DetailsLists/ShimmeredDetailsListWrapper';
import { getCircleCheckIconStyles } from '../EntityTerminationStyles';
import CanAccess from '../../common/canAccess/CanAccess';
import DormancyAssessmentPanel from './DormancyAssessmentPanel';
import ReviewComponent from '../../reviews/ReviewComponent';
import RejectButtonMode from '../../reviews/RejectButtonMode';
import EntityTerminationLegalStructureCard from './EntityTerminationLegalStructureCard';
import { GET_AGM_TRACKER_STATUSES_QUERY } from '../../../utils/statApi/CompanyFiscalYearsApi';
import EntityTerminationReviewApprovalPanel from './EntityTerminationReviewApprovalPanel';
import { IAGMStatusSummaryData, IDormancyAssessmentData } from '../types';
import EntityTerminationReviewRejectPanel from './EntityTerminationReviewRejectPanel';
import UserContext from '../../../utils/authorization/UserContext';
import { StatUser } from '../../../utils/statApi/UsersApi';
import hasAccess from '../../../utils/authorization/authorizationCheck';

interface IEntityTerminationEligibilityReviewProps {
  loading?: boolean;
  companyCode: string;
  companyId?: number;
}

const useEligibilityStyles = makeStyles({
  emptyState: { padding: '20px', textAlign: 'center' },
});

const EntityTerminationEligibilityReview = (
  props: IEntityTerminationEligibilityReviewProps,
): JSX.Element => {
  const { loading: parentLoading, companyCode, companyId } = props;
  const eligibilityClasses = useEligibilityStyles();
  const user = useContext(UserContext) as unknown as StatUser;
  const canViewProcesses = hasAccess(user?.permissions, 'ProcessesList:View');

  const theme = useTheme();

  const [isDormancyAssessmentPanelOpen, setIsDormancyAssessmentPanelOpen] = useState(false);
  const [isReviewApprovalPanelOpen, setIsReviewApprovalPanelOpen] = useState(false);
  const [isReviewRejectPanelOpen, setIsReviewRejectPanelOpen] = useState(false);

  const circleCheckIconStyles = getCircleCheckIconStyles(theme);

  const { data, loading, error } = useQuery(GET_ENTITY_TERMINATION_COMPLIANCE_PROCESSES, {
    variables: {
      companyCode,
    },
    skip: parentLoading, // Skip query if parent is still loading
  });

  const {
    data: agmData,
    loading: agmLoading,
    error: agmError,
  } = useQuery(GET_ENTITY_TERMINATION_AGM_STATUS_SUMMARY, {
    variables: {
      companyCode,
    },
    skip: parentLoading, // Skip query if parent is still loading
  });

  const {
    data: agmStatusesData,
    loading: agmStatusesLoading,
    error: agmStatusesError,
  } = useQuery(GET_AGM_TRACKER_STATUSES_QUERY, {
    skip: parentLoading, // Skip query if parent is still loading
  });

  const review = useQuery(GET_ENTITY_TERMINATION_ELIGIBILITY_REVIEW, {
    fetchPolicy: 'network-only',
    variables: {
      companyCode,
    },
    skip: parentLoading,
  });

  const processesData: IProcessData[] =
    data?.entityTerminationComplianceProcesses?.filter(
      (process: IProcessData) => process.onTrack > 0 || process.late > 0,
    ) ?? [];

  const agmStatusSummaryData: IAGMStatusSummaryData[] =
    agmData?.entityTerminationAGMStatusSummary ?? [];

  const queriedDormancyData = useQuery(GET_DORMANCY_ASSESSMENT, {
    variables: {
      companyCode,
    },
    skip: parentLoading,
  });

  const companyLegalStructureQuery = useQuery(GET_COMPANY_LEGAL_STRUCTURE, {
    variables: {
      companyId,
    },
    skip: parentLoading,
  });

  const companyLegalStructureData = companyLegalStructureQuery?.data?.companyLegalStructure;

  // Transform AGM status summary data into table format
  const getAGMTableData = (): IAGMDetailsData[] => {
    if (!agmStatusSummaryData || agmStatusSummaryData.length === 0) {
      return [];
    }

    // Get counts for each status
    const blockerCount =
      agmStatusSummaryData.find((item) => item.agmStatus === 'Blocker')?.processCount || 0;
    const inProcessCount =
      agmStatusSummaryData.find((item) => item.agmStatus === 'In Process')?.processCount || 0;

    // Calculate others count (all statuses except Blocker and In Process)
    const othersCount = agmStatusSummaryData
      .filter((item) => item.agmStatus !== 'Blocker' && item.agmStatus !== 'In Process')
      .reduce((sum, item) => sum + item.processCount, 0);

    // Create table rows, filtering out zero values
    const tableData: IAGMDetailsData[] = [];

    if (blockerCount > 0) {
      tableData.push({ agmStatus: 'Blocker', processCount: blockerCount });
    }

    if (inProcessCount > 0) {
      tableData.push({ agmStatus: 'In Process', processCount: inProcessCount });
    }

    if (othersCount > 0) {
      tableData.push({ agmStatus: 'Others', processCount: othersCount });
    }

    return tableData;
  };

  const agmTableData = getAGMTableData();

  const processesColumns = getComplianceSnapshotColumns(companyCode, canViewProcesses);

  const agmStatuses = agmStatusesData?.agmTrackerStatuses || [];
  // Only generate AGM columns when we have companyId and AGM statuses
  const agmDetailsColumns =
    companyId && agmStatuses.length > 0
      ? getComplianceSnapshotAGMDetailsColumns(companyId, agmStatuses)
      : [];

  const isLoading = parentLoading || loading;
  const isAgmLoading = parentLoading || agmLoading || agmStatusesLoading;

  const renderProcessesContent = (): JSX.Element => {
    if (processesData.length === 0 && !isLoading) {
      return (
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: 8 }}
          className={eligibilityClasses.emptyState}
        >
          <CheckmarkCircleRegular className={circleCheckIconStyles} />
          <span>All processes are complete</span>
        </Stack>
      );
    }

    return (
      <ShimmeredDetailsListWrapper
        columns={processesColumns}
        items={processesData}
        enableShimmer={isLoading}
        singularListLabel="Process"
        pluralListLabel="Processes"
      />
    );
  };

  const renderAGMDetailsContent = (): JSX.Element => {
    if (agmTableData.length === 0 && !isAgmLoading) {
      return (
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: 8 }}
          className={eligibilityClasses.emptyState}
        >
          <CheckmarkCircleRegular className={circleCheckIconStyles} />
          <span>All AGM details are complete</span>
        </Stack>
      );
    }

    return (
      <ShimmeredDetailsListWrapper
        columns={agmDetailsColumns}
        items={agmTableData}
        enableShimmer={isAgmLoading}
        singularListLabel="AGM Status"
        pluralListLabel="AGM Statuses"
      />
    );
  };

  const renderActionButton = (label: string): JSX.Element => {
    return (
      <>
        <CanAccess requestedAction="DormancyAssessment:Edit">
          <Button
            appearance="subtle"
            title={`Edit ${label}`}
            aria-label={label}
            icon={<EditRegular color={iconButtonStyles.color} />}
            onClick={(): void => {
              setIsDormancyAssessmentPanelOpen(true);
            }}
          />
        </CanAccess>
      </>
    );
  };
  const closePanel = (): void => {
    setIsDormancyAssessmentPanelOpen(false);
    setIsReviewApprovalPanelOpen(false);
    setIsReviewRejectPanelOpen(false);
  };

  const dormancyData: IDormancyAssessmentData = {
    id: queriedDormancyData?.data?.dormancyAssessment?.id,
    employees: queriedDormancyData?.data?.dormancyAssessment?.hasEmployees,
    revenue: queriedDormancyData?.data?.dormancyAssessment?.hasRevenue,
    operatingExpenses: queriedDormancyData?.data?.dormancyAssessment?.hasOperatingExpenses,
    comments: queriedDormancyData?.data?.dormancyAssessment?.comments,
    documentName: queriedDormancyData?.data?.dormancyAssessment?.mercuryDocumentLink,
    documentUrl: queriedDormancyData?.data?.dormancyAssessment?.mercuryDocumentLink,
  };

  const renderDormancyAssessmentContent = (): JSX.Element => {
    return (
      <Stack horizontal wrap horizontalAlign="space-between">
        <StackColumn>
          <SpanWithLabel labelText="Employees:" value={dormancyData.employees} />
          <SpanWithLabel labelText="Revenue:" value={dormancyData.revenue} />
          <SpanWithLabel labelText="Operating Expenses:" value={dormancyData.operatingExpenses} />
        </StackColumn>

        <StackColumn>
          <SpanWithLabel labelText="Comments:" value={dormancyData.comments} />
          <SpanWithLabel labelText="Mercury Link:">
            <SafeLink
              url={dormancyData.documentUrl}
              title={dormancyData.documentUrl}
              style={truncatedLinkStyle}
              aria-label={`Mercury document link ${dormancyData.documentUrl}`}
            />
          </SpanWithLabel>
        </StackColumn>
      </Stack>
    );
  };

  const onApprovalOverrideMethod = (): void => {
    setIsReviewApprovalPanelOpen(true);
  };

  const onRejectOverrideMethod = (): void => {
    setIsReviewRejectPanelOpen(true);
  };

  return (
    <div className={`${bodyContentContainer} lightBackground`}>
      <LoadingErrorMessage
        loading={loading || agmLoading}
        error={error || agmError || agmStatusesError}
      />
      <Stack horizontal wrap horizontalAlign="space-between" tokens={{ childrenGap: 20 }}>
        <StackColumn>
          <Card title="Statutory Compliance Snapshot - Processes" isLoading={isLoading}>
            {renderProcessesContent()}
          </Card>
        </StackColumn>
        <StackColumn>
          <Card
            title="Dormancy Assessment"
            isLoading={false}
            actionButton={renderActionButton('Dormancy Assessment Info')}
          >
            {renderDormancyAssessmentContent()}
          </Card>
          {isDormancyAssessmentPanelOpen && (
            <DormancyAssessmentPanel
              closePanel={closePanel}
              dormancyData={queriedDormancyData.data?.dormancyAssessment}
              dormancyAssessmentRequest={{
                id: dormancyData.id,
                companyCode,
                hasEmployees: dormancyData.employees,
                hasRevenue: dormancyData.revenue,
                hasOperatingExpenses: dormancyData.operatingExpenses,
                comments: dormancyData.comments,
                mercuryDocumentLink: dormancyData.documentUrl,
              }}
              refetchQueries={[
                {
                  query: GET_DORMANCY_ASSESSMENT,
                  variables: { companyCode },
                },
              ]}
            />
          )}
        </StackColumn>
      </Stack>
      <br />
      <Stack horizontal wrap horizontalAlign="space-between" tokens={{ childrenGap: 20 }}>
        <StackColumn>
          <EntityTerminationLegalStructureCard
            loading={parentLoading}
            data={companyLegalStructureData}
          />
        </StackColumn>
        <StackColumn>
          <Card title="Statutory Compliance Snapshot - AGM" isLoading={isAgmLoading}>
            {renderAGMDetailsContent()}
          </Card>
        </StackColumn>
      </Stack>
      <br />
      <>
        <ReviewComponent
          review={review.data?.entityTerminationEligibilityReview?.review}
          approveMutation={APPROVE_ENTITY_TERMINATION_ELIGIBILITY_REVIEW}
          rejectMutation={REJECT_ENTITY_TERMINATION_ELIGIBILITY_REVIEW}
          loading={loading}
          createCommentMutation={CREATE_ENTITY_TERMINATION_ELIGIBILITY_REVIEW_COMMENT}
          attachDocumentMutation={ATTACH_ENTITY_TERMINATION_ELIGIBILITY_REVIEW_DOCUMENT}
          rejectButtonMode={RejectButtonMode.Reject}
          onApprovalOverrideMethod={onApprovalOverrideMethod}
          onRejectOverrideMethod={onRejectOverrideMethod}
          displayOnly={!review.data?.entityTerminationEligibilityReview?.hasActiveEligibilityReview}
        />
        {isReviewApprovalPanelOpen && (
          <EntityTerminationReviewApprovalPanel
            closePanel={closePanel}
            data={review.data?.entityTerminationEligibilityReview}
          />
        )}
        {isReviewRejectPanelOpen && (
          <EntityTerminationReviewRejectPanel
            closePanel={closePanel}
            data={review.data?.entityTerminationEligibilityReview}
          />
        )}
      </>
    </div>
  );
};

export default EntityTerminationEligibilityReview;
